import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';
import { elearningAPI } from '../../services/elearningApi';
import { Course, CourseFilters, CourseDifficulty, Enrollment } from '../../types/elearning';
import CourseCard from '../../components/elearning/CourseCard';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import ErrorState from '../../components/ui/ErrorState';
import EmptyState from '../../components/ui/EmptyState';
import { useAccessLevel } from '../../hooks/useAccessLevel';
import { AccessLevel } from '../../types/access';
import { Audience, ClinicalArea, Topic } from '../../types/metadata';
import { audienceConfig, clinicalAreaLabels, formatTopicLabel } from '../../utils/metadataHelpers';

function facetSelectOptions(placeholder: string, entries: Array<[string, string]>) {
  return [
    { value: '', label: placeholder },
    ...entries
      .filter(([value]) => value !== 'Unknown')
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label })),
  ];
}

const AUDIENCE_FILTERS = facetSelectOptions(
  'All audiences',
  Object.values(Audience).map((value) => [value, audienceConfig[value].label] as [string, string])
);
const TOPIC_FILTERS = facetSelectOptions(
  'All topics',
  Object.values(Topic).map((value) => [value, formatTopicLabel(value)] as [string, string])
);
const CLINICAL_AREA_FILTERS = facetSelectOptions(
  'All clinical areas',
  Object.values(ClinicalArea).map((value) => [value, clinicalAreaLabels[value]] as [string, string])
);

type ViewMode = 'grid' | 'list';

export default function ELearning() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<CourseFilters>({});
  const { accessLevel, isAuthenticated } = useAccessLevel();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch courses
        const fetchedCourses = await elearningAPI.fetchCourses(filters);
        setCourses(fetchedCourses);

        // Fetch user enrollments if authenticated
        if (isAuthenticated) {
          const userEnrollments = await elearningAPI.getUserEnrollments();
          setEnrollments(userEnrollments);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load courses'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, isAuthenticated]);

  // Apply search filter
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchQuery || undefined,
      }));
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleDifficultyChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      difficulty: value ? (value as CourseDifficulty) : undefined,
    }));
  };

  const handleFacetChange = (key: 'audiences' | 'topics' | 'clinicalAreas', value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value ? [value] : undefined,
    }));
  };

  const myCourses = enrollments
    .filter((e) => e.status !== 'CANCELLED')
    .map((enrollment) => {
      const course = courses.find((c) => c.id === enrollment.courseId);
      return course ? { course, enrollment } : null;
    })
    .filter((item): item is { course: Course; enrollment: Enrollment } => item !== null);

  const allCourses = courses.filter((course) => {
    const enrollment = enrollments.find((e) => e.courseId === course.id);
    return !enrollment || enrollment.status === 'CANCELLED';
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={5} className="mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <LoadingSkeleton key={i} lines={8} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">eLearning</h1>
          <p className="text-gray-600">
            Browse our comprehensive course library and enhance your knowledge
          </p>
        </div>
        {isAuthenticated && (
          <Link
            to="/elearning/certifications"
            className="font-medium text-primary hover:text-primary-dark transition-colors"
          >
            Certificates
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <div className="mb-8 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={filters.difficulty || ''}
            onChange={(e) => handleDifficultyChange(e.target.value)}
            className="w-full sm:w-48"
            options={[
              { value: '', label: 'All Difficulties' },
              { value: CourseDifficulty.BEGINNER, label: 'Beginner' },
              { value: CourseDifficulty.INTERMEDIATE, label: 'Intermediate' },
              { value: CourseDifficulty.ADVANCED, label: 'Advanced' },
            ]}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Select
            value={filters.audiences?.[0] || ''}
            onChange={(e) => handleFacetChange('audiences', e.target.value)}
            options={AUDIENCE_FILTERS}
          />
          <Select
            value={filters.topics?.[0] || ''}
            onChange={(e) => handleFacetChange('topics', e.target.value)}
            options={TOPIC_FILTERS}
          />
          <Select
            value={filters.clinicalAreas?.[0] || ''}
            onChange={(e) => handleFacetChange('clinicalAreas', e.target.value)}
            options={CLINICAL_AREA_FILTERS}
          />
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {courses.length} {courses.length === 1 ? 'course' : 'courses'} found
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              aria-label="Grid view"
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              aria-label="List view"
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* My Courses Section */}
      {isAuthenticated && myCourses.length > 0 && (
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">My Courses</h2>
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {myCourses.map(({ course, enrollment }) => (
              <CourseCard
                key={course.id}
                course={course}
                enrollment={enrollment}
                view={viewMode}
              />
            ))}
          </div>
        </div>
      )}

      {/* All Courses Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            {isAuthenticated && myCourses.length > 0 ? 'All Courses' : 'Courses'}
          </h2>
          {accessLevel === AccessLevel.Admin && (
            <div className="flex items-center gap-4">
              <Link
                to="/elearning/admin/courses"
                className="font-medium text-primary hover:text-primary-dark transition-colors"
              >
                Manage courses
              </Link>
              <Link
                to="/elearning/admin/courses/new"
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors font-medium"
              >
                Create Course
              </Link>
            </div>
          )}
        </div>

        {allCourses.length === 0 ? (
          <EmptyState
            title="No courses found"
            description="Try adjusting your search or filters to find what you're looking for."
          />
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {allCourses.map((course) => (
              <CourseCard key={course.id} course={course} view={viewMode} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

